/**
 * The settled clause (D247) — what a which-one answer settles about a clause
 * outlives the answer, for as long as the clause is still being asked about.
 *
 * @remarks
 * D79 resolves a which-one answer without text surgery: the asking clause is
 * read again against a catalog with the LOSING candidates removed, and the
 * chosen thing wins its word uncontested. Until D247 the removal lived for
 * that one reading. Mike, playing the Meridian with both keys carried:
 * `use key` asked which, `revival key` answered it, the clause asked "Use it
 * on what?" — and the answer, `pod`, was spliced onto the clause as typed,
 * `use key on pod`, read against the whole room, and asked which key again.
 * The question had forgotten the answer that earned it.
 *
 * So the settlement is carried as data: a question asked of a settled clause
 * holds the refs the player has ruled out for it (`ruledOutRefs`), and every
 * reading of that clause — the answer's splice, the re-read that asks
 * whether the world has settled the question, the in-context refusals —
 * reads it against the world less those things. Still no text surgery: the
 * parser stays the judge, of the clause the player is actually answering.
 * It accumulates down the conversation, so a which-one on the SECOND object
 * (`use key` → `revival` → "Use it on what?" → `cabinet` → which cabinet?)
 * carries the first answer into the second question, and the last answer
 * reads the clause with both settled.
 *
 * @packageDocumentation
 */

import type { RuntimeState } from '@orpheus/content-schema';

import { CLARIFICATION_SCORE_WINDOW, type EntityRef, type ParseCandidateEntity } from '@orpheus/parser-engine';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';
import type { PendingQuestion, PendingWhichEntityQuestion } from './pending-question.js';
import type { PlayerInputOutcome } from './player-input-outcome.js';

import { deriveEntityCatalog } from '../entities/derive-entity-catalog.js';

/**
 * How far below the thing chosen for the clause a ruled-out thing the answer
 * names again is ranked: twice the parser's which-one window, so the
 * clause's own word never ties with it.
 */
const ANSWERED_AGAIN_DROP = 2 * CLARIFICATION_SCORE_WINDOW;

/** What the player has ruled out for one clause — plain-string refs, as the schema keeps them; absent when nothing is. */
export type RuledOutRefs = PendingWhichEntityQuestion['ruledOutRefs'];

/**
 * The catalog a clause is read against: the world as it is now, less what
 * the player has ruled out for that clause.
 *
 * @remarks
 * Ruled out, not ranked down: a thing the player declined is never chosen
 * for the clause in their stead, so when the chosen thing has gone the
 * clause names nothing and says so (D230's settled-by-the-world rule), where
 * a demotion would quietly read the word as the thing they turned down.
 *
 * The one exception is an ANSWER that names a ruled-out thing by words of
 * its own: `use key` → `revival` → "Use it on what?" → `the hex key`. The
 * clause's `key` is still the revival key, and the answer's `hex key` is the
 * other one — two things that share a noun, meeting in one sentence. That
 * thing stays in the catalog, ranked below the chosen one by more than the
 * parser's which-one window, so the clause's bare word still selects the
 * thing chosen for it and only the answer's own words reach the other.
 *
 * @param bundle - The loaded bundle
 * @param state - The state to read the world from
 * @param ruledOutRefs - What the player has ruled out for the clause, if anything
 * @param answeredRef - The thing an answer spliced onto the clause names by its own words, if any
 * @returns The referable things the clause may name
 */
export const clauseCatalogOf = (
	bundle: EngineGameBundle,
	state: RuntimeState,
	ruledOutRefs: RuledOutRefs,
	answeredRef?: EntityRef,
): readonly ParseCandidateEntity[] => {
	const catalog = deriveEntityCatalog(bundle, state);

	if (ruledOutRefs === undefined) {
		return catalog;
	}

	const ruledOut = new Set<string>(ruledOutRefs);

	return catalog.flatMap((entity) => {
		if (!ruledOut.has(entity.entityRef)) {
			return [entity];
		}

		return entity.entityRef === answeredRef ? [{ ...entity, salience: entity.salience - ANSWERED_AGAIN_DROP }] : [];
	});
};

/**
 * What a which-one answer rules out for its clause: whatever the clause had
 * already settled, and every candidate the answer did not choose.
 *
 * @param question - The parked which-one question
 * @param chosenRef - The candidate the answer named
 * @returns The refs the clause is read without from now on
 */
export const ruledOutByAnswer = (question: PendingWhichEntityQuestion, chosenRef: EntityRef): NonNullable<RuledOutRefs> => [
	...(question.ruledOutRefs ?? []),
	...question.candidates
		.map((candidate) => candidate.entityRef)
		.filter((candidateRef) => candidateRef !== (chosenRef as unknown as string)),
];

/**
 * The outcome of reading a settled clause, with the question it asked next
 * — a completion, or a which-one about another of its words — carrying the
 * settlement, so the answer to THAT question reads the clause the same way.
 *
 * @remarks
 * The caller decides the question was the settled clause's own: a which-one
 * answer re-runs one clause and nothing else; a completion's answer is the
 * head of whatever the player typed, so a question there is its own only
 * when the walk stopped on it (D91's line — a turn consumed before the stop
 * means a later clause of the player's chain asked).
 *
 * @param outcome - The outcome, its questions already parked
 * @param ruledOutRefs - What the clause was read without, if anything
 * @returns The outcome, its parked clause question carrying the settlement
 */
export const carryingSettlement = (outcome: PlayerInputOutcome, ruledOutRefs: RuledOutRefs): PlayerInputOutcome => {
	if (ruledOutRefs === undefined) {
		return outcome;
	}

	if (outcome.type === 'executed') {
		return { ...outcome, result: { ...outcome.result, nextState: settledState(outcome.result.nextState, ruledOutRefs) }};
	}

	return { ...outcome, nextState: settledState(outcome.nextState, ruledOutRefs) };
};

/**
 * The state with its parked clause question carrying the settlement — a
 * question with no clause to read (a confirm, an actor's) or none at all is
 * left as it is.
 *
 * @param state - The state after the run
 * @param ruledOutRefs - What the clause was read without
 * @returns The state, settled
 */
const settledState = (state: RuntimeState, ruledOutRefs: NonNullable<RuledOutRefs>): RuntimeState => {
	const question: PendingQuestion | undefined = state.session.pendingQuestion;

	if (question?.kind !== 'completion' && question?.kind !== 'which_entity') {
		return state;
	}

	return { ...state, session: { ...state.session, pendingQuestion: { ...question, ruledOutRefs }}};
};
